import { cva, type VariantProps } from '@veraclins-dev/cva';
import { cn } from '@veraclins-dev/utils';

import { emphasisActionVariants } from './base';
import { LINK_DEFAULT_CLASSES, PRESS_FEEDBACK_CLASSES } from './shared-classes';
import { styleProps } from './styles';

/** ::::::::: Link — Model A :::::::::
 * `type="link"` is an inline hyperlink: it speaks `tone` only, defaulting to the
 * brand colour, with `neutral` (foreground) and `danger` as the alternatives.
 * `inherit` takes the colour, size and line height of the surrounding text, for
 * links on a surface with its own text colour (alerts, notices, a site footer).
 * `success` is also accepted, for the few positive actions styled as links.
 * `inherit` is a link-only tone: Button's shared tone axis does not carry it, and
 * a `type="button"` link with `tone="inherit"` gets no surface.
 * `type="button"` is an action styled as a button: it speaks the same
 * emphasis × tone axes as Button (`emphasis` filled/soft/outline/quiet/ghost,
 * `tone` neutral/brand/danger/success).
 * Both types show press feedback on `:active`: an inline link dims to 70%
 * opacity and a button link shares `PRESS_FEEDBACK_CLASSES` with Button, so it
 * scales down by the same amount over the same duration.
 */
const linkVariants = cva({
  base: LINK_DEFAULT_CLASSES,
  responsive: {
    ...styleProps,
  },
  variants: {
    // Press feedback shows on :active, so a tap on a touch screen responds as
    // the finger lands. An inline link dims; a button link reuses Button's own
    // press feedback rather than restating it.
    type: {
      // No touch hit area: WCAG 2.5.8 exempts a link in a sentence, and a
      // widened hit area made a tap on one stacked link open the next. A list
      // of standalone links gets its spacing from the layout instead.
      link: 'border-0 w-fit justify-start active:opacity-70',
      button: cn(
        'border border-transparent rounded-md text-sm font-medium ring-offset-background',
        PRESS_FEEDBACK_CLASSES,
      ),
    },
    ...emphasisActionVariants.variants,
    tone: {
      ...emphasisActionVariants.variants.tone,
      // The focus ring follows the text colour, so it shows on any surface.
      inherit:
        'text-inherit text-[length:inherit] leading-[inherit] focus-visible:ring-current',
    },
    underline: {
      none: '',
      hover: '',
      always: '',
    },
    linkSize: {
      sm: '',
      md: '',
      lg: '',
      xl: '',
    },
  },
  compoundVariants: [
    // type="button": the shared action compounds (emphasis × tone).
    ...emphasisActionVariants.compoundVariants.map((variant) => ({
      ...variant,
      type: 'button' as const,
    })),
    // type="button" sizes, the same heights as Button's, touch minimum included.
    {
      type: 'button',
      linkSize: 'sm',
      className:
        'min-h-8 py-1 px-2 pointer-coarse:min-h-(--touch-min) pointer-coarse:min-w-(--touch-min)',
    },
    {
      type: 'button',
      linkSize: 'md',
      className:
        'min-h-10 px-4 py-2 pointer-coarse:min-h-(--touch-min) pointer-coarse:min-w-(--touch-min)',
    },
    { type: 'button', linkSize: 'lg', className: 'min-h-12 py-3 px-6' },
    { type: 'button', linkSize: 'xl', className: 'min-h-14 py-4 px-8' },
    // type="link" inline colour by tone (default brand).
    { type: 'link', tone: 'neutral', className: 'text-foreground' },
    { type: 'link', tone: 'brand', className: 'text-primary' },
    { type: 'link', tone: 'danger', className: 'text-danger' },
    { type: 'link', tone: 'success', className: 'text-success' },
    // type="link" underline treatment (border tracks the text colour).
    {
      type: 'link',
      underline: 'hover',
      className: 'border-b-2 border-transparent hover:border-current',
    },
    {
      type: 'link',
      underline: 'always',
      className: 'border-b-2 border-current',
    },
    // An always-underlined inherit link cannot change colour on hover, so
    // hover doubles the underline with a text-coloured shadow below the
    // border. It does not move the layout or lower the text contrast the way
    // a tint or an opacity change would. `underline="hover"` already gains its
    // line on hover, and `underline="none"` gets no hover line at all.
    {
      type: 'link',
      tone: 'inherit',
      underline: 'always',
      className: 'hover:shadow-[0_2px_0_0_currentColor]',
    },
  ],
  defaultVariants: {
    type: 'link',
    // Consumed by type="button" links; inert for inline links.
    emphasis: 'filled',
    tone: 'brand',
    underline: 'hover',
    linkSize: 'md',
  },
});

type LinkVariants = VariantProps<typeof linkVariants>;

export { type LinkVariants, linkVariants };
